// ============================================================================
// POKER MATH ENGINE
// ============================================================================
// Core poker mathematics: equity calculations, pot odds, EV, hand ranges
// All calculations are deterministic - no LLM needed
// ============================================================================

import { Card } from '../../types/GameState';

// ============================================================================
// TYPES
// ============================================================================

export interface EquityResult {
  equity: number;           // 0-100 percentage
  outs: number;
  description: string;
  calculation: string;      // Show the math
}

export interface PotOddsResult {
  potOdds: number;          // e.g., 3.0 means 3:1
  potOddsPercent: number;   // As percentage needed to call
  callProfitable: boolean;
  description: string;
  calculation: string;
}

export interface EVResult {
  ev: number;               // Expected value in dollars
  evPercent: number;        // As percentage of pot
  decision: 'profitable' | 'breakeven' | 'unprofitable';
  description: string;
  calculation: string;
}

export interface BetSizingResult {
  recommended: number;
  sizing: 'small' | 'medium' | 'large' | 'overbet';
  potPercentage: number;
  reasoning: string;
}

export interface HandRange {
  hands: string[];
  percentage: number;
  description: string;
}

export interface ImpliedOddsResult {
  requiredImpliedOdds: number;
  stackToCall: number;
  isProfitable: boolean;
  description: string;
}

// ============================================================================
// CONSTANTS
// ============================================================================

// Standard outs for common draws
export const DRAW_OUTS: Record<string, number> = {
  'flush-draw': 9,
  'oesd': 8,           // Open-ended straight draw
  'gutshot': 4,
  'combo-draw': 15,    // Flush + OESD
  'backdoor-flush': 1.5,
  'backdoor-straight': 1,
  'two-overcards': 6,
  'set-draw': 2,       // Pocket pair to trips
  'two-pair-draw': 4,  // One pair to two pair
};

// Position-based opening ranges (percentage of hands)
export const POSITION_RANGES: Record<string, { range: number; hands: string[] }> = {
  'UTG': {
    range: 12,
    hands: ['AA', 'KK', 'QQ', 'JJ', 'TT', 'AKs', 'AKo', 'AQs', 'AQo', 'AJs', 'KQs'],
  },
  'UTG+1': {
    range: 14,
    hands: ['AA', 'KK', 'QQ', 'JJ', 'TT', '99', 'AKs', 'AKo', 'AQs', 'AQo', 'AJs', 'ATs', 'KQs', 'KQo'],
  },
  'MP': {
    range: 18,
    hands: ['AA', 'KK', 'QQ', 'JJ', 'TT', '99', '88', 'AKs', 'AKo', 'AQs', 'AQo', 'AJs', 'ATs', 'A9s', 'KQs', 'KQo', 'KJs', 'QJs'],
  },
  'MP+1': {
    range: 20,
    hands: ['AA', 'KK', 'QQ', 'JJ', 'TT', '99', '88', '77', 'AKs', 'AKo', 'AQs', 'AQo', 'AJs', 'ATs', 'A9s', 'A8s', 'KQs', 'KQo', 'KJs', 'KTs', 'QJs', 'QTs'],
  },
  'CO': {
    range: 27,
    hands: ['AA', 'KK', 'QQ', 'JJ', 'TT', '99', '88', '77', '66', 'AKs', 'AKo', 'AQs', 'AQo', 'AJs', 'ATs', 'A9s', 'A8s', 'A7s', 'A5s', 'A4s', 'KQs', 'KQo', 'KJs', 'KTs', 'K9s', 'QJs', 'QTs', 'JTs', 'T9s', '98s'],
  },
  'BTN': {
    range: 40,
    hands: ['AA', 'KK', 'QQ', 'JJ', 'TT', '99', '88', '77', '66', '55', '44', 'AKs', 'AKo', 'AQs', 'AQo', 'AJs', 'AJo', 'ATs', 'ATo', 'A9s', 'A8s', 'A7s', 'A6s', 'A5s', 'A4s', 'A3s', 'A2s', 'KQs', 'KQo', 'KJs', 'KJo', 'KTs', 'K9s', 'K8s', 'QJs', 'QJo', 'QTs', 'Q9s', 'JTs', 'J9s', 'T9s', 'T8s', '98s', '87s', '76s', '65s', '54s'],
  },
  'SB': {
    range: 35,
    hands: ['AA', 'KK', 'QQ', 'JJ', 'TT', '99', '88', '77', '66', '55', 'AKs', 'AKo', 'AQs', 'AQo', 'AJs', 'AJo', 'ATs', 'A9s', 'A8s', 'A7s', 'A5s', 'A4s', 'KQs', 'KQo', 'KJs', 'KTs', 'K9s', 'QJs', 'QTs', 'JTs', 'T9s', '98s', '87s', '76s'],
  },
  'BB': {
    range: 100, // Defending range depends heavily on action
    hands: ['Wide - depends on raise size and position of raiser'],
  },
};

// Hand vs Hand equity (common matchups)
export const HAND_MATCHUPS: Record<string, Record<string, number>> = {
  'AA': { 'KK': 82, 'QQ': 82, 'AKs': 87, 'AKo': 88, 'random': 85 },
  'KK': { 'AA': 18, 'QQ': 82, 'AKs': 66, 'AKo': 69, 'random': 82 },
  'QQ': { 'AA': 18, 'KK': 18, 'AKs': 54, 'AKo': 57, 'random': 80 },
  'JJ': { 'AA': 18, 'KK': 18, 'QQ': 18, 'AKs': 54, 'random': 77 },
  'AKs': { 'AA': 13, 'KK': 34, 'QQ': 46, 'JJ': 46, '22': 48, 'random': 67 },
  'AKo': { 'AA': 12, 'KK': 31, 'QQ': 43, 'JJ': 43, '22': 45, 'random': 65 },
};

// ============================================================================
// CORE CALCULATION FUNCTIONS
// ============================================================================

/**
 * Calculate equity based on outs
 * Rule of 2 (one card) and Rule of 4 (two cards)
 */
export function calculateEquity(
  outs: number,
  cardsTocome: 1 | 2
): EquityResult {
  let equity: number;
  let formula: string;
  
  if (cardsTocome === 2) {
    // Rule of 4: outs × 4 (flop to river)
    // More accurate formula: 1 - ((47-outs)/47) × ((46-outs)/46)
    const accurate = (1 - ((47 - outs) / 47) * ((46 - outs) / 46)) * 100;
    equity = Math.min(accurate, 100);
    formula = `Rule of 4: ${outs} outs × 4 ≈ ${outs * 4}% (exact: ${accurate.toFixed(1)}%)`;
  } else {
    // Rule of 2: outs × 2 (turn to river)
    // More accurate: outs / 46 × 100
    const accurate = (outs / 46) * 100;
    equity = Math.min(accurate, 100);
    formula = `Rule of 2: ${outs} outs × 2 ≈ ${outs * 2}% (exact: ${accurate.toFixed(1)}%)`;
  }
  
  let description = '';
  if (equity >= 50) description = 'Strong favorite to improve';
  else if (equity >= 35) description = 'Solid drawing odds';
  else if (equity >= 20) description = 'Marginal draw';
  else description = 'Weak draw - need good pot odds or implied odds';
  
  return {
    equity: Math.round(equity * 10) / 10,
    outs,
    description,
    calculation: formula,
  };
}

/**
 * Calculate pot odds
 */
export function calculatePotOdds(
  potSize: number,
  callAmount: number
): PotOddsResult {
  if (callAmount <= 0) {
    return {
      potOdds: Infinity,
      potOddsPercent: 0,
      callProfitable: true,
      description: 'Free to see the next card (check available)',
      calculation: 'No bet to call',
    };
  }
  
  const totalPot = potSize + callAmount;
  const potOdds = totalPot / callAmount;
  const potOddsPercent = (callAmount / totalPot) * 100;
  
  const calculation = `Pot odds = (Pot + Call) / Call = ($${potSize} + $${callAmount}) / $${callAmount} = ${potOdds.toFixed(2)}:1\nYou need ${potOddsPercent.toFixed(1)}% equity to call profitably`;
  
  return {
    potOdds: Math.round(potOdds * 100) / 100,
    potOddsPercent: Math.round(potOddsPercent * 10) / 10,
    callProfitable: false, // Need to compare with equity
    description: `Getting ${potOdds.toFixed(1)}:1 odds - need ${potOddsPercent.toFixed(0)}% equity to call`,
    calculation,
  };
}

/**
 * Compare equity vs pot odds to determine if call is profitable
 */
export function isCallProfitable(
  equity: number,
  potSize: number,
  callAmount: number
): { profitable: boolean; ev: number; explanation: string } {
  const odds = calculatePotOdds(potSize, callAmount);
  const requiredEquity = odds.potOddsPercent;
  const profitable = equity >= requiredEquity;
  
  // EV = (Equity × Pot Won) - ((1 - Equity) × Amount Lost)
  // Simplified: EV = Equity × (Pot + Call) - Call
  const potWon = potSize + callAmount;
  const ev = (equity / 100) * potWon - callAmount;
  
  let explanation = '';
  if (profitable) {
    explanation = ` PROFITABLE CALL: Your ${equity.toFixed(1)}% equity beats the ${requiredEquity.toFixed(1)}% needed. EV = +$${ev.toFixed(2)} per call.`;

  } else {
    const deficitPct = requiredEquity - equity;
    explanation = ` UNPROFITABLE CALL: Your ${equity.toFixed(1)}% equity is ${deficitPct.toFixed(1)}% short of the ${requiredEquity.toFixed(1)}% needed. EV = -$${Math.abs(ev).toFixed(2)} per call.`;

  }
  
  return { profitable, ev, explanation };
}

/**
 * Calculate expected value of a bet/raise
 */
export function calculateBetEV(params: {
  potSize: number;
  betSize: number;
  foldEquity: number;  // 0-100, chance opponent folds
  equityWhenCalled: number;  // 0-100
}): EVResult {
  const { potSize, betSize, foldEquity, equityWhenCalled } = params;
  
  // EV = (Fold% × Pot) + (Call% × (Equity × (Pot + 2×Bet) - Bet))
  const foldPct = foldEquity / 100;
  const callPct = 1 - foldPct;
  const eqPct = equityWhenCalled / 100;
  
  const evWhenFold = foldPct * potSize;
  const potIfCalled = potSize + (2 * betSize);
  const evWhenCalled = callPct * ((eqPct * potIfCalled) - betSize);
  const totalEV = evWhenFold + evWhenCalled;
  
  const evPercent = (totalEV / potSize) * 100;
  
  const calculation = `EV = (Fold% × Pot) + (Call% × (Equity × TotalPot - Bet))
EV = (${(foldPct * 100).toFixed(0)}% × $${potSize}) + (${(callPct * 100).toFixed(0)}% × (${equityWhenCalled}% × $${potIfCalled} - $${betSize}))
EV = $${evWhenFold.toFixed(2)} + $${evWhenCalled.toFixed(2)} = $${totalEV.toFixed(2)}`;
  
  let decision: 'profitable' | 'breakeven' | 'unprofitable';
  let description: string;
  
  if (totalEV > betSize * 0.05) {
    decision = 'profitable';
    description = `Betting is +EV ($${totalEV.toFixed(2)} expected gain)`;
  } else if (totalEV < -betSize * 0.05) {
    decision = 'unprofitable';
    description = `Betting is -EV ($${Math.abs(totalEV).toFixed(2)} expected loss)`;
  } else {
    decision = 'breakeven';
    description = 'Betting is approximately breakeven';
  }
  
  return {
    ev: Math.round(totalEV * 100) / 100,
    evPercent: Math.round(evPercent * 10) / 10,
    decision,
    description,
    calculation,
  };
}

/**
 * Calculate recommended bet sizing
 */
export function calculateBetSizing(params: {
  potSize: number;
  street: 'flop' | 'turn' | 'river';
  purpose: 'value' | 'bluff' | 'protection' | 'probe';
  boardTexture: 'dry' | 'wet' | 'semi-wet';
  position: 'ip' | 'oop'; // In position / Out of position
}): BetSizingResult {
  const { potSize, street, purpose, boardTexture, position } = params;
  
  let multiplier: number;
  let sizing: 'small' | 'medium' | 'large' | 'overbet';
  let reasoning: string;
  
  // Default sizing based on purpose
  if (purpose === 'value') {
    // Value bets: larger on wet boards (charge draws), smaller on dry (get calls)
    if (boardTexture === 'wet') {
      multiplier = 0.75;
      sizing = 'large';
      reasoning = 'Large value bet on wet board - charge draws and get value from worse made hands';
    } else if (boardTexture === 'semi-wet') {
      multiplier = 0.6;
      sizing = 'medium';
      reasoning = 'Medium value bet - balance between value and keeping worse hands in';
    } else {
      multiplier = 0.5;
      sizing = 'medium';
      reasoning = 'Medium bet on dry board - keep weaker hands calling';
    }
  } else if (purpose === 'bluff') {
    // Bluffs: smaller is more efficient (risk less to win pot)
    if (street === 'river') {
      multiplier = 0.66;
      sizing = 'medium';
      reasoning = 'River bluff: 2/3 pot is efficient - puts opponent in tough spot without over-risking';
    } else {
      multiplier = 0.5;
      sizing = 'small';
      reasoning = 'Earlier street bluff: smaller sizing keeps bluffs efficient with draws behind';
    }
  } else if (purpose === 'protection') {
    multiplier = 0.75;
    sizing = 'large';
    reasoning = 'Protection bet: deny equity to drawing hands with larger sizing';
  } else {
    // Probe
    multiplier = 0.33;
    sizing = 'small';
    reasoning = 'Probe bet: small sizing to gather information cheaply';
  }
  
  // Adjust for position
  if (position === 'oop') {
    multiplier *= 1.1; // Bet slightly larger OOP to discourage floats
    reasoning += ' (Adjusted up 10% for OOP - discourage floats)';
  }
  
  // Adjust for street
  if (street === 'river' && purpose === 'value') {
    multiplier *= 1.1; // Size up value bets on river
    reasoning += ' (River: can size up value bets)';
  }
  
  const recommended = Math.round(potSize * multiplier);
  
  return {
    recommended,
    sizing,
    potPercentage: Math.round(multiplier * 100),
    reasoning,
  };
}

/**
 * Calculate implied odds requirements
 */
export function calculateImpliedOdds(params: {
  potSize: number;
  callAmount: number;
  equity: number;  // Current equity (with draw)
}): ImpliedOddsResult {
  const { potSize, callAmount, equity } = params;
  
  // Current pot odds needed
  const directOddsNeeded = (callAmount / (potSize + callAmount)) * 100;
  
  // If direct odds are good, no implied odds needed
  if (equity >= directOddsNeeded) {
    return {
      requiredImpliedOdds: 0,
      stackToCall: 0,
      isProfitable: true,
      description: 'Direct pot odds are sufficient - implied odds are a bonus',
    };
  }
  
  // Calculate how much more we need to win
  // Required total pot = Call / (Equity / 100)
  const requiredTotalPot = callAmount / (equity / 100);
  const additionalNeeded = requiredTotalPot - (potSize + callAmount);
  const stackMultiplier = additionalNeeded / callAmount;
  
  return {
    requiredImpliedOdds: Math.round(additionalNeeded * 100) / 100,
    stackToCall: Math.round(stackMultiplier * 100) / 100,
    isProfitable: additionalNeeded > 0 && additionalNeeded < callAmount * 10,
    description: `Need to win an additional $${additionalNeeded.toFixed(2)} on later streets (${stackMultiplier.toFixed(1)}x the call) to make this profitable`,
  };
}

/**
 * Get opening range for a position
 */
export function getOpeningRange(position: string): HandRange {
  const positionKey = position.toUpperCase().replace('-', '').replace('+', '+1');
  const range = POSITION_RANGES[positionKey] || POSITION_RANGES['MP'];
  
  return {
    hands: range.hands,
    percentage: range.range,
    description: `From ${position}, open approximately ${range.range}% of hands: ${range.hands.slice(0, 8).join(', ')}...`,
  };
}

/**
 * Check if a hand should be in a range
 */
export function isHandInRange(
  hand: string,  // e.g., "AKs", "QQ", "T9o"
  position: string
): { inRange: boolean; reason: string } {
  const range = getOpeningRange(position);
  
  // Normalize hand notation
  const normalized = normalizeHand(hand);
  
  if (range.hands.includes(normalized)) {
    return {
      inRange: true,
      reason: `${hand} is a standard open from ${position}`,
    };
  }
  
  // Check for suited/offsuit variants
  const base = normalized.slice(0, 2);
  const suited = base + 's';
  const offsuit = base + 'o';
  
  if (range.hands.includes(suited) && !normalized.includes('o')) {
    return {
      inRange: true,
      reason: `${hand} suited is playable from ${position}`,
    };
  }
  
  return {
    inRange: false,
    reason: `${hand} is too weak to open from ${position}. Standard range is ${range.percentage}% of hands.`,
  };
}

/**
 * Normalize hand notation
 */
function normalizeHand(hand: string): string {
  // Handle various formats: "AK", "AKs", "AKo", "Ak suited", etc.
  let h = hand.toUpperCase().replace(/SUITED/i, 's').replace(/OFFSUIT/i, 'o').replace(/\s/g, '');
  
  // Ensure high card first
  const ranks = 'AKQJT98765432';
  if (h.length >= 2) {
    const first = h[0];
    const second = h[1];
    if (ranks.indexOf(second) < ranks.indexOf(first)) {
      h = second + first + h.slice(2);
    }
  }
  
  return h;
}

/**
 * Calculate minimum defense frequency (MDF)
 */
export function calculateMDF(betSize: number, potSize: number): {
  mdf: number;
  description: string;
} {
  // MDF = Pot / (Pot + Bet)
  const mdf = (potSize / (potSize + betSize)) * 100;
  
  return {
    mdf: Math.round(mdf * 10) / 10,
    description: `Against a $${betSize} bet into $${potSize} pot, you should defend at least ${mdf.toFixed(0)}% of your range to prevent opponent from profiting with any two cards.`,
  };
}

/**
 * Get common hand vs hand equity
 */
export function getHeadsUpEquity(hand1: string, hand2: string): {
  equity: number;
  description: string;
} {
  const h1 = normalizeHand(hand1);
  const h2 = normalizeHand(hand2);
  
  // Check direct matchup
  if (HAND_MATCHUPS[h1]?.[h2]) {
    return {
      equity: HAND_MATCHUPS[h1][h2],
      description: `${h1} vs ${h2}: ${HAND_MATCHUPS[h1][h2]}% equity`,
    };
  }
  
  // Check reverse
  if (HAND_MATCHUPS[h2]?.[h1]) {
    return {
      equity: 100 - HAND_MATCHUPS[h2][h1],
      description: `${h1} vs ${h2}: ${100 - HAND_MATCHUPS[h2][h1]}% equity`,
    };
  }
  
  // Generic estimations
  // Pair vs overcards ≈ 55%
  // Pair vs undercards ≈ 80%
  // Dominated hands ≈ 70% for dominator
  
  return {
    equity: 50, // Default to coin flip
    description: `${h1} vs ${h2}: Approximately 50% (coin flip) - check a poker equity calculator for exact odds`,
  };
}

// ============================================================================
// UTILITY EXPORTS
// ============================================================================

export const pokerMath = {
  calculateEquity,
  calculatePotOdds,
  isCallProfitable,
  calculateBetEV,
  calculateBetSizing,
  calculateImpliedOdds,
  getOpeningRange,
  isHandInRange,
  calculateMDF,
  getHeadsUpEquity,
  DRAW_OUTS,
  POSITION_RANGES,
  HAND_MATCHUPS,
};

export default pokerMath;